import axios from 'axios';
import { tokenService } from './tokenService';

// Use relative URL in production (empty REACT_APP_BACKEND_URL), or the env variable for development
const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Configure axios defaults for cookie support
axios.defaults.withCredentials = true;

// Axios interceptor for auth (backward compatibility with Authorization header)
axios.interceptors.request.use((config) => {
  const token = tokenService.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// API functions
export const api = {
  // Auth
  login: (data) => axios.post(`${API_URL}/auth/login`, data),
  register: (data) => axios.post(`${API_URL}/auth/register`, data),
  getMe: () => axios.get(`${API_URL}/auth/me`),
  
  // Password Reset & Email Confirmation
  forgotPassword: (email) => axios.post(`${API_URL}/auth/forgot-password`, { email }),
  resetPassword: (token, new_password) => axios.post(`${API_URL}/auth/reset-password`, { token, new_password }),
  verifyResetToken: (token) => axios.get(`${API_URL}/auth/verify-reset-token/${token}`),
  confirmEmail: (token) => axios.post(`${API_URL}/auth/confirm-email`, { token }),
  resendConfirmation: (email) => axios.post(`${API_URL}/auth/resend-confirmation`, { email }),

  // Agents
  getAgents: () => axios.get(`${API_URL}/agents`),
  createAgent: (data) => axios.post(`${API_URL}/agents`, data),
  updateAgent: (id, data) => axios.put(`${API_URL}/agents/${id}`, data),
  toggleAgentStatus: (id) => axios.put(`${API_URL}/agents/${id}/status`),
  deleteAgent: (id) => axios.delete(`${API_URL}/agents/${id}`),

  // Secteurs
  getSecteurs: () => axios.get(`${API_URL}/secteurs`),
  createSecteur: (data) => axios.post(`${API_URL}/secteurs`, data),
  updateSecteur: (id, data) => axios.put(`${API_URL}/secteurs/${id}`, data),
  deleteSecteur: (id) => axios.delete(`${API_URL}/secteurs/${id}`),
  assignSecteur: (secteurId, agentId) => axios.put(`${API_URL}/secteurs/${secteurId}/assign/${agentId}`),
  unassignSecteur: (secteurId) => axios.put(`${API_URL}/secteurs/${secteurId}/unassign`),

  // Coproprietes
  getCoproprietes: (params) => axios.get(`${API_URL}/coproprietes`, { params }),
  getCopropriete: (id) => axios.get(`${API_URL}/coproprietes/${id}`),
  createCopropriete: (data) => axios.post(`${API_URL}/coproprietes`, data),
  updateCopropriete: (id, data) => axios.put(`${API_URL}/coproprietes/${id}`, data),
  deleteCopropriete: (id) => axios.delete(`${API_URL}/coproprietes/${id}`),

  // Opportunites
  getOpportunites: (params) => axios.get(`${API_URL}/opportunites`, { params }),
  createOpportunite: (data) => axios.post(`${API_URL}/opportunites`, data),
  updateOpportunite: (id, data) => axios.put(`${API_URL}/opportunites/${id}`, data),
  deleteOpportunite: (id) => axios.delete(`${API_URL}/opportunites/${id}`),

  // Actions
  getActions: (params) => axios.get(`${API_URL}/actions`, { params }),
  createAction: (data) => axios.post(`${API_URL}/actions`, data),

  // Syndics
  getSyndics: () => axios.get(`${API_URL}/syndics`),
  createSyndic: (data) => axios.post(`${API_URL}/syndics`, data),
  updateSyndic: (id, data) => axios.put(`${API_URL}/syndics/${id}`, data),
  deleteSyndic: (id) => axios.delete(`${API_URL}/syndics/${id}`),

  // Dashboard
  getDashboardStats: () => axios.get(`${API_URL}/dashboard/stats`),

  // Import
  importCoproprietes: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/import/coproprietes`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importDPE: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/import/dpe`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importRegistre: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/import/registre`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Registre National
  searchRegistre: (params) => axios.get(`${API_URL}/registre/search`, { params }),
  getRegistreStats: () => axios.get(`${API_URL}/registre/stats`),
  getFilterOptions: () => axios.get(`${API_URL}/registre/filter-options`),
  getCoproDPEAggregate: (numeroImmat) => axios.get(`${API_URL}/registre/${numeroImmat}/dpe-aggregate`),
  getCoproDPEMatches: (numeroImmat) => axios.get(`${API_URL}/registre/${numeroImmat}/dpe-matches`),
  getCodesPostaux: () => axios.get(`${API_URL}/registre/codes-postaux`),
  getDepartements: () => axios.get(`${API_URL}/registre/departements`),
  getSyndicsParCP: (codePostal) => axios.get(`${API_URL}/registre/syndics-par-cp`, { params: { code_postal: codePostal } }),
  getSyndicsParDepartement: (departement) => axios.get(`${API_URL}/registre/syndics-par-departement`, { params: { departement } }),

  // Entreprise API (SIRET lookup)
  lookupEntrepriseBySiret: (siret) => axios.get(`${API_URL}/entreprise/siret/${siret}`),

  // DPE ADEME API
  searchDPE: (codePostal, adresse) => axios.get(`${API_URL}/dpe/search`, { params: { code_postal: codePostal, adresse } }),
  enrichProspectionDPE: (prospectionId) => axios.post(`${API_URL}/prospections/${prospectionId}/enrich-dpe`),

  // Interactions tracking
  trackInteraction: (prospectionId, data) => axios.post(`${API_URL}/prospections/${prospectionId}/interactions`, data),
  getInteractions: (prospectionId) => axios.get(`${API_URL}/prospections/${prospectionId}/interactions`),
  
  // Agenda
  getAgendaActions: (startDate, endDate, agentFilter) => axios.get(`${API_URL}/agenda/actions`, { 
    params: { start_date: startDate, end_date: endDate, agent_filter: agentFilter } 
  }),
  getAgendaKpis: () => axios.get(`${API_URL}/agenda/kpis`),
  reassignAction: (actionId, newAgentId) => axios.put(`${API_URL}/agenda/actions/${actionId}/reassign`, null, {
    params: { new_agent_id: newAgentId }
  }),
  createAgendaEvent: (data) => axios.post(`${API_URL}/agenda`, data),
  getAgendaEvents: (params) => axios.get(`${API_URL}/agenda`, { params }),

  // Import DPE
  importDPESource: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/import/dpe-source`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Admin DPE/Matching
  normalizeRegistre: () => axios.post(`${API_URL}/admin/normalize-registre`),
  runMatching: (departement) => axios.post(`${API_URL}/admin/run-matching`, null, { params: departement ? { departement } : {} }),
  getDPEStats: () => axios.get(`${API_URL}/admin/dpe-stats`),
  
  // ADEME Mass Enrichment
  startAdemeEnrichment: (params) => axios.post(`${API_URL}/admin/ademe-enrichment/start`, null, { params }),
  stopAdemeEnrichment: () => axios.post(`${API_URL}/admin/ademe-enrichment/stop`),
  getAdemeEnrichmentStatus: () => axios.get(`${API_URL}/admin/ademe-enrichment/status`),

  // Prospections
  getProspections: (params) => axios.get(`${API_URL}/prospections`, { params }),
  getAllProspections: (params) => axios.get(`${API_URL}/prospections`, { params: { limit: 1000, ...params } }),
  createProspections: (numeros) => axios.post(`${API_URL}/prospections`, { numeros_immatriculation: numeros }),
  createManualProspection: (data) => axios.post(`${API_URL}/prospections/manual`, data),
  updateProspection: (id, data) => axios.put(`${API_URL}/prospections/${id}`, data),
  deleteProspection: (id) => axios.delete(`${API_URL}/prospections/${id}`),
  getProspectionById: (id) => axios.get(`${API_URL}/prospections/${id}`),
  getProspectionsStats: (params) => axios.get(`${API_URL}/prospections/stats`, { params }),
  reassignProspection: (id, newAgentId) => axios.post(`${API_URL}/prospections/${id}/reassign`, null, { params: { new_agent_id: newAgentId } }),
  
  // Diagnostic tools
  diagnosticSearchProspection: (search) => axios.get(`${API_URL}/prospections/diagnostic/by-name`, { params: { search } }),
  diagnosticCheckVisibility: (search, agentEmail) => axios.get(`${API_URL}/prospections/diagnostic/check-visibility`, { params: { search, agent_email: agentEmail } }),
  forceReassignProspection: (prospectionId, newAgentId) => axios.post(`${API_URL}/prospections/diagnostic/force-reassign`, null, { params: { prospection_id: prospectionId, new_agent_id: newAgentId } }),
  
  // Prospection Interactions
  getInteractions: (prospectionId) => axios.get(`${API_URL}/prospections/${prospectionId}/interactions`),
  addInteraction: (prospectionId, data) => axios.post(`${API_URL}/prospections/${prospectionId}/interactions`, data),

  // Prospection Documents
  getProspectionDocuments: (prospectionId) => axios.get(`${API_URL}/prospections/${prospectionId}/documents`),
  uploadProspectionDocument: (prospectionId, formData) => axios.post(
    `${API_URL}/prospections/${prospectionId}/documents`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  ),
  downloadProspectionDocument: (prospectionId, documentId) => axios.get(
    `${API_URL}/prospections/${prospectionId}/documents/${documentId}/download`,
    { responseType: 'blob' }
  ),
  deleteProspectionDocument: (prospectionId, documentId) => axios.delete(
    `${API_URL}/prospections/${prospectionId}/documents/${documentId}`
  ),

  // CEE Settings
  getCEESettings: () => axios.get(`${API_URL}/cee-settings`),
  getActiveCEESettings: () => axios.get(`${API_URL}/cee-settings/active`),
  createCEESettings: (data) => axios.post(`${API_URL}/cee-settings`, data),
  updateCEESettings: (id, data) => axios.put(`${API_URL}/cee-settings/${id}`, data),
  deleteCEESettings: (id) => axios.delete(`${API_URL}/cee-settings/${id}`),

  // Gamification Settings
  getGamificationSettings: () => axios.get(`${API_URL}/gamification-settings`),
  getAllGamificationSettings: () => axios.get(`${API_URL}/gamification-settings/all`),
  createGamificationSettings: (data) => axios.post(`${API_URL}/gamification-settings`, data),
  updateGamificationSettings: (id, data) => axios.put(`${API_URL}/gamification-settings/${id}`, data),
  deleteGamificationSettings: (id) => axios.delete(`${API_URL}/gamification-settings/${id}`),
  activateGamificationSettings: (id) => axios.post(`${API_URL}/gamification-settings/${id}/activate`),

  // Maintenance
  checkExpirations: () => axios.post(`${API_URL}/maintenance/check-expirations`),

  // Admin Advanced Dashboard
  getAdminAdvancedDashboard: () => axios.get(`${API_URL}/admin/dashboard/advanced`),

  // ==================== SAAS ====================
  // Pricing (public)
  getSaasPricing: () => axios.get(`${API_URL}/saas/pricing`),
  
  // Company Registration (public)
  registerCompany: (data) => axios.post(`${API_URL}/saas/register`, data),
  
  // Payment
  initiatePayment: (companyId, originUrl) => axios.post(`${API_URL}/saas/payment/initiate`, {
    company_id: companyId,
    origin_url: originUrl
  }),
  checkPaymentStatus: (sessionId) => axios.get(`${API_URL}/saas/payment/status/${sessionId}`),
  
  // Super Admin - Companies
  getAllCompanies: () => axios.get(`${API_URL}/saas/admin/companies`),
  getAllOrders: () => axios.get(`${API_URL}/saas/admin/orders`),
  getPendingOrders: () => axios.get(`${API_URL}/saas/admin/orders/pending`),
  getSaasStats: () => axios.get(`${API_URL}/saas/admin/stats`),
  suspendCompany: (id) => axios.put(`${API_URL}/saas/admin/companies/${id}/suspend`),
  activateCompany: (id) => axios.put(`${API_URL}/saas/admin/companies/${id}/activate`),
  deleteCompany: (id) => axios.delete(`${API_URL}/saas/admin/companies/${id}`),
  updateCompanyLicenses: (id, nombre_licences, billing_plan) => 
    axios.put(`${API_URL}/saas/admin/companies/${id}/licenses?nombre_licences=${nombre_licences}${billing_plan ? `&billing_plan=${billing_plan}` : ''}`),

  // ==================== LICENSES ====================
  getLicenseStatus: () => axios.get(`${API_URL}/saas/licenses/status`),
  buyLicenses: (data) => axios.post(`${API_URL}/saas/licenses/buy`, data),
  checkLicensePayment: (sessionId) => axios.get(`${API_URL}/saas/licenses/payment-status/${sessionId}`),
  
  // License Management
  getOrderHistory: () => axios.get(`${API_URL}/saas/orders/history`),
  scheduleLicenseReduction: (count) => axios.post(`${API_URL}/saas/licenses/schedule-reduction?nombre_licences_to_remove=${count}`),
  cancelLicenseReduction: () => axios.delete(`${API_URL}/saas/licenses/cancel-reduction`),
  getScheduledChanges: () => axios.get(`${API_URL}/saas/licenses/scheduled-changes`),
  createBillingPortalSession: (returnUrl) => axios.post(`${API_URL}/saas/billing/portal-session?return_url=${encodeURIComponent(returnUrl)}`),
  
  // License Batches (Option A - Factures séparées par date d'achat)
  getLicenseBatches: () => axios.get(`${API_URL}/saas/licenses/batches`),
  getLicenseBatchesSummary: () => axios.get(`${API_URL}/saas/licenses/batches/summary`),
  scheduleBatchReduction: (batchId, count) => axios.post(`${API_URL}/saas/licenses/batches/${batchId}/schedule-reduction?nombre_licences_to_remove=${count}`),
  cancelBatchReduction: (batchId) => axios.delete(`${API_URL}/saas/licenses/batches/${batchId}/cancel-reduction`),
  migrateToBatches: () => axios.post(`${API_URL}/saas/licenses/migrate-to-batches`),
  
  // Invoices (Factures Stripe)
  getInvoices: () => axios.get(`${API_URL}/saas/invoices`),
  
  // Payment Alerts (Admin Dashboard)
  getPaymentAlerts: () => axios.get(`${API_URL}/saas/payment-alerts`),
  resolvePaymentAlert: (alertId) => axios.post(`${API_URL}/saas/payment-alerts/${alertId}/resolve`),
  
  // Payment Method
  getPaymentMethod: () => axios.get(`${API_URL}/saas/billing/payment-method`),

  // ==================== MY COMPANY (Admin) ====================
  getMyCompany: () => axios.get(`${API_URL}/saas/my-company`),
  updateMyCompany: (data) => axios.put(`${API_URL}/saas/my-company`, data),

  // ==================== SUPPORT TICKETS ====================
  // Client endpoints
  createTicket: (data) => axios.post(`${API_URL}/tickets`, data),
  getMyTickets: (status) => axios.get(`${API_URL}/tickets`, { params: status ? { status } : {} }),
  getTicket: (id) => axios.get(`${API_URL}/tickets/${id}`),
  replyToTicket: (id, content) => axios.post(`${API_URL}/tickets/${id}/reply`, { content }),
  
  // Super Admin - Tickets
  getAllTickets: (params) => axios.get(`${API_URL}/tickets/admin/all`, { params }),
  updateTicket: (id, data) => axios.put(`${API_URL}/tickets/admin/${id}`, data),
  getTicketStats: () => axios.get(`${API_URL}/tickets/admin/stats`),

  // ==================== PLATFORM SETTINGS ====================
  getPlatformSettings: () => axios.get(`${API_URL}/settings/platform`),
  updateCompanySettings: (data) => axios.put(`${API_URL}/settings/platform/company`, data),
  updateBankSettings: (data) => axios.put(`${API_URL}/settings/platform/bank`, data),
  updateStripeSettings: (data) => axios.put(`${API_URL}/settings/platform/stripe`, data),
  updateEmailSettings: (data) => axios.put(`${API_URL}/settings/platform/email`, data),
  updateBrandingSettings: (data) => axios.put(`${API_URL}/settings/platform/branding`, data),
  uploadLogo: (formData) => axios.post(`${API_URL}/settings/platform/logo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteLogo: () => axios.delete(`${API_URL}/settings/platform/logo`),
  testStripeConnection: () => axios.get(`${API_URL}/settings/platform/stripe/test`),

  // ==================== LEGAL PAGES ====================
  getLegalPage: (pageType) => axios.get(`${API_URL}/legal-pages/${pageType}`),
  updateLegalPage: (pageType, content) => axios.put(`${API_URL}/legal-pages/${pageType}`, { content }),
  resetLegalPage: (pageType) => axios.delete(`${API_URL}/legal-pages/${pageType}`),

  // ==================== EMAIL TEMPLATES ====================
  getEmailTemplates: () => axios.get(`${API_URL}/emails/templates`),
  getEmailVariables: () => axios.get(`${API_URL}/emails/templates/variables`),
  createEmailTemplate: (data) => axios.post(`${API_URL}/emails/templates`, data),
  updateEmailTemplate: (id, data) => axios.put(`${API_URL}/emails/templates/${id}`, data),
  deleteEmailTemplate: (id) => axios.delete(`${API_URL}/emails/templates/${id}`),
  duplicateEmailTemplate: (id) => axios.post(`${API_URL}/emails/templates/${id}/duplicate`),
  uploadTemplateAttachment: (templateId, formData) => axios.post(
    `${API_URL}/emails/templates/${templateId}/attachments`, 
    formData, 
    { headers: { 'Content-Type': 'multipart/form-data' } }
  ),
  deleteTemplateAttachment: (templateId, index) => axios.delete(`${API_URL}/emails/templates/${templateId}/attachments/${index}`),
  
  // Email Sending
  sendProspectEmail: (data) => axios.post(`${API_URL}/emails/send`, data),
  previewEmail: (formData) => axios.post(`${API_URL}/emails/preview`, formData),
  getEmailHistory: (prospectionId) => axios.get(`${API_URL}/emails/history/${prospectionId}`),
  
  // Subscription Products (SuperAdmin)
  getSubscriptionProducts: (includeInactive = false) => axios.get(`${API_URL}/subscription-products?include_inactive=${includeInactive}`),
  getSubscriptionProduct: (id) => axios.get(`${API_URL}/subscription-products/${id}`),
  createSubscriptionProduct: (data) => axios.post(`${API_URL}/subscription-products`, data),
  updateSubscriptionProduct: (id, data) => axios.put(`${API_URL}/subscription-products/${id}`, data),
  deleteSubscriptionProduct: (id, hardDelete = false) => axios.delete(`${API_URL}/subscription-products/${id}?hard_delete=${hardDelete}`),
  activateSubscriptionProduct: (id) => axios.post(`${API_URL}/subscription-products/${id}/activate`),
  getSubscriptionProductsHistory: (productId = null, limit = 50) => {
    const url = productId 
      ? `${API_URL}/subscription-products/history?product_id=${productId}&limit=${limit}`
      : `${API_URL}/subscription-products/history?limit=${limit}`;
    return axios.get(url);
  },
  
  // ==================== MODULE TRAVAUX ====================
  
  // Licence Travaux
  getTravauxLicenceStatus: () => axios.get(`${API_URL}/travaux/licence/status`),
  getTravauxLicencePricing: () => axios.get(`${API_URL}/travaux/licence/pricing`),
  createTravauxLicenceCheckout: (data) => axios.post(`${API_URL}/travaux/licence/checkout`, data),
  checkTravauxLicencePaymentStatus: (sessionId) => axios.get(`${API_URL}/travaux/licence/payment-status/${sessionId}`),
  activateTravauxLicence: (nombre_licences, plan = 'monthly') => 
    axios.post(`${API_URL}/travaux/licence/activate?nombre_licences=${nombre_licences}&plan=${plan}`),
  forceActivateTravauxLicence: (companyId, nombreLicences = 1, plan = 'monthly') => 
    axios.post(`${API_URL}/travaux/licence/force-activate?company_id=${companyId}&nombre_licences=${nombreLicences}&plan=${plan}`),
  
  // Gestion des utilisateurs licence
  getTravauxLicenceUsers: () => axios.get(`${API_URL}/travaux/licence/users`),
  updateTravauxUserAccess: (userId, hasAccess) => 
    axios.put(`${API_URL}/travaux/licence/users/${userId}`, { user_id: userId, has_access: hasAccess }),
  addTravauxLicences: (nombreLicences) => 
    axios.post(`${API_URL}/travaux/licence/add-licences?nombre_licences=${nombreLicences}`),
  
  // Planning Gantt
  getTravauxGanttData: (projetId = null) => 
    axios.get(`${API_URL}/travaux/planning/gantt${projetId ? `?projet_id=${projetId}` : ''}`),
  updateTravauxGanttTask: (taskId, taskType, data) => 
    axios.put(`${API_URL}/travaux/planning/task/${taskId}?task_type=${taskType}`, data),
  
  // GED - Documents
  getTravauxDocuments: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.type_document) queryParams.append('type_document', params.type_document);
    if (params.statut) queryParams.append('statut', params.statut);
    if (params.sous_traitant_id) queryParams.append('sous_traitant_id', params.sous_traitant_id);
    return axios.get(`${API_URL}/travaux/ged/documents?${queryParams.toString()}`);
  },
  getTravauxDocumentsStats: () => axios.get(`${API_URL}/travaux/ged/stats`),
  getTravauxDocumentsAlertes: (limit = 10) => axios.get(`${API_URL}/travaux/ged/alertes?limit=${limit}`),
  updateTravauxDocument: (docId, sousTraitantId, data) => 
    axios.put(`${API_URL}/travaux/ged/documents/${docId}?sous_traitant_id=${sousTraitantId}`, data),
  
  // Groupes de postes
  getGroupesPostes: (projetId) => axios.get(`${API_URL}/travaux/projets/${projetId}/groupes`),
  createGroupePoste: (projetId, data) => axios.post(`${API_URL}/travaux/projets/${projetId}/groupes`, data),
  updateGroupePoste: (projetId, groupeId, data) => axios.put(`${API_URL}/travaux/projets/${projetId}/groupes/${groupeId}`, data),
  deleteGroupePoste: (projetId, groupeId, movePostesTo = null) => {
    const params = movePostesTo ? `?move_postes_to=${movePostesTo}` : '';
    return axios.delete(`${API_URL}/travaux/projets/${projetId}/groupes/${groupeId}${params}`);
  },
  
  // Projets Travaux
  getProjetsTravaux: (params) => axios.get(`${API_URL}/travaux/projets`, { params }),
  getProjetsTravauxStats: () => axios.get(`${API_URL}/travaux/projets/stats`),
  getProjetTravaux: (id) => axios.get(`${API_URL}/travaux/projets/${id}`),
  createProjetTravaux: (data) => axios.post(`${API_URL}/travaux/projets`, data),
  updateProjetTravaux: (id, data) => axios.put(`${API_URL}/travaux/projets/${id}`, data),
  deleteProjetTravaux: (id) => axios.delete(`${API_URL}/travaux/projets/${id}`),
  
  // Études de faisabilité
  previewEtudeFaisabilite: (projetId) => axios.post(`${API_URL}/travaux/projets/${projetId}/etude-faisabilite/preview`),
  downloadEtudeFaisabilite: (projetId) => axios.get(`${API_URL}/travaux/projets/${projetId}/etude-faisabilite`, { responseType: 'blob' }),
  
  // Postes Travaux
  createPosteTravaux: (projetId, data) => axios.post(`${API_URL}/travaux/projets/${projetId}/postes`, data),
  updatePosteTravaux: (posteId, data) => axios.put(`${API_URL}/travaux/postes/${posteId}`, data),
  deletePosteTravaux: (posteId) => axios.delete(`${API_URL}/travaux/postes/${posteId}`),
  duplicatePosteTravaux: (posteId) => axios.post(`${API_URL}/travaux/postes/${posteId}/duplicate`),
  
  // Sous-Traitants
  getSousTraitants: (params) => axios.get(`${API_URL}/travaux/sous-traitants`, { params }),
  getSousTraitant: (id) => axios.get(`${API_URL}/travaux/sous-traitants/${id}`),
  createSousTraitant: (data) => axios.post(`${API_URL}/travaux/sous-traitants`, data),
  updateSousTraitant: (id, data) => axios.put(`${API_URL}/travaux/sous-traitants/${id}`, data),
  deleteSousTraitant: (id) => axios.delete(`${API_URL}/travaux/sous-traitants/${id}`),
  addDocumentSousTraitant: (stId, type, nom, url, dateValidite) => 
    axios.post(`${API_URL}/travaux/sous-traitants/${stId}/documents?type_document=${type}&nom_fichier=${encodeURIComponent(nom)}&url=${encodeURIComponent(url)}${dateValidite ? `&date_validite=${dateValidite}` : ''}`),
  deleteDocumentSousTraitant: (stId, docId) => 
    axios.delete(`${API_URL}/travaux/sous-traitants/${stId}/documents/${docId}`),
  
  // Import de devis
  analyzeDevisForImport: (projetId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/travaux/projets/${projetId}/import-devis/analyze`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  confirmDevisImport: (projetId, groupes, metadata = {}) => 
    axios.post(`${API_URL}/travaux/projets/${projetId}/import-devis/confirm`, { 
      projet_id: projetId, 
      groupes,
      ...metadata 
    }),
  
  // Devis importés (convertis en postes)
  getDevisProjet: (projetId) =>
    axios.get(`${API_URL}/travaux/projets/${projetId}/devis`),
  getDevisDetail: (projetId, devisId) =>
    axios.get(`${API_URL}/travaux/projets/${projetId}/devis/${devisId}`),
  deleteDevisImporte: (projetId, devisId, supprimerPostes = false) =>
    axios.delete(`${API_URL}/travaux/projets/${projetId}/devis/${devisId}?supprimer_postes=${supprimerPostes}`),
  
  // Devis archivés (stockés sans conversion)
  getDevisArchives: (projetId) =>
    axios.get(`${API_URL}/travaux/projets/${projetId}/devis-archives`),
  getDevisArchiveDetail: (projetId, devisId) =>
    axios.get(`${API_URL}/travaux/projets/${projetId}/devis-archives/${devisId}`),
  createDevisArchive: (projetId, data) =>
    axios.post(`${API_URL}/travaux/projets/${projetId}/devis-archives`, data),
  updateDevisArchive: (projetId, devisId, data) =>
    axios.put(`${API_URL}/travaux/projets/${projetId}/devis-archives/${devisId}`, data),
  deleteDevisArchive: (projetId, devisId) =>
    axios.delete(`${API_URL}/travaux/projets/${projetId}/devis-archives/${devisId}`),
  convertirDevisArchive: (projetId, devisId) =>
    axios.post(`${API_URL}/travaux/projets/${projetId}/devis-archives/${devisId}/convertir`),
  
  // Notes de postes (historique)
  getNotesPoste: (posteId) =>
    axios.get(`${API_URL}/travaux/postes/${posteId}/notes`),
  createNotePoste: (posteId, data) =>
    axios.post(`${API_URL}/travaux/postes/${posteId}/notes`, data),
  updateNotePoste: (posteId, noteId, data) =>
    axios.put(`${API_URL}/travaux/postes/${posteId}/notes/${noteId}`, data),
  deleteNotePoste: (posteId, noteId) =>
    axios.delete(`${API_URL}/travaux/postes/${posteId}/notes/${noteId}`),
  
  // Documents de postes
  getDocumentsPoste: (posteId) =>
    axios.get(`${API_URL}/travaux/postes/${posteId}/documents`),
  uploadDocumentPoste: (posteId, formData) =>
    axios.post(`${API_URL}/travaux/postes/${posteId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  deleteDocumentPoste: (posteId, docId) =>
    axios.delete(`${API_URL}/travaux/postes/${posteId}/documents/${docId}`),
  getDocumentDownloadUrl: (posteId, docId) =>
    `${API_URL}/travaux/postes/${posteId}/documents/${docId}/download`,
  getDocumentPreviewUrl: (posteId, docId) =>
    `${API_URL}/travaux/postes/${posteId}/documents/${docId}/preview`,
  
  // Paiements sous-traitants
  getPaiementsPoste: (posteId) =>
    axios.get(`${API_URL}/travaux/postes/${posteId}/paiements`),
  createPaiementPoste: (posteId, data) =>
    axios.post(`${API_URL}/travaux/postes/${posteId}/paiements`, data),
  updatePaiementPoste: (posteId, paiementId, data) =>
    axios.put(`${API_URL}/travaux/postes/${posteId}/paiements/${paiementId}`, data),
  updateStatutPaiement: (posteId, paiementId, statut) =>
    axios.put(`${API_URL}/travaux/postes/${posteId}/paiements/${paiementId}/statut?statut=${statut}`),
  deletePaiementPoste: (posteId, paiementId) =>
    axios.delete(`${API_URL}/travaux/postes/${posteId}/paiements/${paiementId}`),
  getPaiementsProjet: (projetId) =>
    axios.get(`${API_URL}/travaux/projets/${projetId}/paiements`),
  
  // Documents de projets (chantiers)
  getDocumentsProjet: (projetId) =>
    axios.get(`${API_URL}/travaux/projets/${projetId}/documents`),
  uploadDocumentProjet: (projetId, formData) =>
    axios.post(`${API_URL}/travaux/projets/${projetId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  deleteDocumentProjet: (projetId, docId) =>
    axios.delete(`${API_URL}/travaux/projets/${projetId}/documents/${docId}`),
  getDocumentProjetDownloadUrl: (projetId, docId) =>
    `${API_URL}/travaux/projets/${projetId}/documents/${docId}/download`,
  getDocumentProjetPreviewUrl: (projetId, docId) =>
    `${API_URL}/travaux/projets/${projetId}/documents/${docId}/preview`,
  
  // Délégataires CEE
  getDelegatairesCEE: (actif = null) => 
    axios.get(`${API_URL}/travaux/delegataires-cee${actif !== null ? `?actif=${actif}` : ''}`),
  createDelegataireCEE: (data) => 
    axios.post(`${API_URL}/travaux/delegataires-cee`, data),
  updateDelegataireCEE: (id, data) => 
    axios.put(`${API_URL}/travaux/delegataires-cee/${id}`, data),
  deleteDelegataireCEE: (id) => 
    axios.delete(`${API_URL}/travaux/delegataires-cee/${id}`),
  
  // Prime CEE Projet
  getPrimeCEEProjet: (projetId) => 
    axios.get(`${API_URL}/travaux/projets/${projetId}/prime-cee`),
  updatePrimeCEEProjet: (projetId, data) => 
    axios.put(`${API_URL}/travaux/projets/${projetId}/prime-cee`, data),
  
  // ==================== FORMULAIRE ÉLIGIBILITÉ ====================
  // Configuration
  getFormulaireConfig: () =>
    axios.get(`${API_URL}/eligibilite/config`),
  updateFormulaireConfig: (statut_minimum, titre_formulaire = null, description = null, modele_document_id = null) => {
    const params = new URLSearchParams();
    if (statut_minimum) params.append('statut_minimum', statut_minimum);
    if (titre_formulaire) params.append('titre_formulaire', titre_formulaire);
    if (description) params.append('description', description);
    if (modele_document_id !== null) params.append('modele_document_id', modele_document_id);
    return axios.put(`${API_URL}/eligibilite/config?${params.toString()}`);
  },
  
  // Admin - Gestion du formulaire
  getEligibiliteCategories: () =>
    axios.get(`${API_URL}/eligibilite/categories`),
  getEligibiliteTypesChamps: () =>
    axios.get(`${API_URL}/eligibilite/types-champs`),
  getEligibiliteChamps: (categorie = null, actifOnly = true) =>
    axios.get(`${API_URL}/eligibilite/champs?actif_only=${actifOnly}${categorie ? `&categorie=${categorie}` : ''}`),
  createEligibiliteChamp: (data) =>
    axios.post(`${API_URL}/eligibilite/champs`, data),
  updateEligibiliteChamp: (champId, data) =>
    axios.put(`${API_URL}/eligibilite/champs/${champId}`, data),
  deleteEligibiliteChamp: (champId) =>
    axios.delete(`${API_URL}/eligibilite/champs/${champId}`),
  reorderEligibiliteChamps: (champsOrdre) =>
    axios.post(`${API_URL}/eligibilite/champs/reorder`, champsOrdre),
  initEligibiliteFormulaire: () =>
    axios.post(`${API_URL}/eligibilite/admin/init-formulaire`),
  
  // Agent - Réponses au formulaire
  getEligibiliteReponses: (prospectionId) =>
    axios.get(`${API_URL}/eligibilite/prospections/${prospectionId}/reponses`),
  saveEligibiliteReponses: (prospectionId, data) =>
    axios.post(`${API_URL}/eligibilite/prospections/${prospectionId}/reponses`, data),
  uploadEligibiliteFichier: (prospectionId, formData) =>
    axios.post(`${API_URL}/eligibilite/prospections/${prospectionId}/fichiers`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  deleteEligibiliteFichier: (fileId) =>
    axios.delete(`${API_URL}/eligibilite/fichiers/${fileId}`),
  getEligibiliteFichierUrl: (fileId) =>
    `${API_URL}/eligibilite/fichiers/${fileId}`,
  
  // Admin - Tableau récapitulatif
  getEligibiliteAllReponses: (page = 1, limit = 20, statut = null) =>
    axios.get(`${API_URL}/eligibilite/admin/reponses?page=${page}&limit=${limit}${statut ? `&statut=${statut}` : ''}`),
  exportEligibiliteReponses: (format = 'json') =>
    axios.get(`${API_URL}/eligibilite/admin/export?format=${format}`),
  
  // Étapes préliminaires du projet (pipeline travaux)
  getEtapesProjet: (projetId) =>
    axios.get(`${API_URL}/travaux/projets/${projetId}/etapes`),
  getEtapeDetail: (projetId, etapeId) =>
    axios.get(`${API_URL}/travaux/projets/${projetId}/etapes/${etapeId}`),
  updateEtape: (projetId, etapeId, data) =>
    axios.put(`${API_URL}/travaux/projets/${projetId}/etapes/${etapeId}`, data),
  uploadDocumentEtape: (projetId, etapeId, formData) =>
    axios.post(`${API_URL}/travaux/projets/${projetId}/etapes/${etapeId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  deleteDocumentEtape: (projetId, etapeId, docId) =>
    axios.delete(`${API_URL}/travaux/projets/${projetId}/etapes/${etapeId}/documents/${docId}`),
  getDocumentEtapeUrl: (projetId, etapeId, docId) =>
    `${API_URL}/travaux/projets/${projetId}/etapes/${etapeId}/documents/${docId}/download`,

  // Réseau de chaleur (France Chaleur Urbaine)
  getReseauChaleurInfo: (projetId) =>
    axios.get(`${API_URL}/travaux/projets/${projetId}/reseau-chaleur`),
  updateAttestationNonRaccordement: (projetId, etapeId, data) =>
    axios.put(`${API_URL}/travaux/projets/${projetId}/etapes/${etapeId}/attestation`, null, { params: data }),

  // Notes d'instruction des étapes
  getNotesEtape: (projetId, etapeId) =>
    axios.get(`${API_URL}/travaux/projets/${projetId}/etapes/${etapeId}/notes`),
  createNoteEtape: (projetId, etapeId, contenu) =>
    axios.post(`${API_URL}/travaux/projets/${projetId}/etapes/${etapeId}/notes`, { contenu }),
  updateNoteEtape: (projetId, etapeId, noteId, contenu) =>
    axios.put(`${API_URL}/travaux/projets/${projetId}/etapes/${etapeId}/notes/${noteId}`, { contenu }),
  deleteNoteEtape: (projetId, etapeId, noteId) =>
    axios.delete(`${API_URL}/travaux/projets/${projetId}/etapes/${etapeId}/notes/${noteId}`),

  // Generic HTTP methods (for flexibility)
  get: (url) => axios.get(`${API_URL}${url}`),
  post: (url, data) => axios.post(`${API_URL}${url}`, data),
  put: (url, data) => axios.put(`${API_URL}${url}`, data),
  delete: (url) => axios.delete(`${API_URL}${url}`),
};

export default api;
